import React, { Component, Fragment } from 'react';
import { GROUP_TYPE, PRODUCT_LIST_TYPE, UserProductGroup } from '~/Api/User/ProductGroup';
import { CreateModal as ProductGroupCreateModal } from '~/ProductGroup';
import ProductGroupSelect from '~/ProductGroupSelect';
import Button from '~/UI/Button';
import linkMiniStyles from '~/UI/Button/link-mini.less';
import buttonPrimaryAltMiniStyles from '~/UI/Button/primary-alt-mini.less';
import buttonPrimarySmallStyles from '~/UI/Button/primary-small.less';
import buttonSecondaryMiniStyles from '~/UI/Button/secondary-mini.less';
import SaveActivityIndicator, { Statuses } from '~/UI/SaveActivityIndicator/SaveActivityIndicator';
import SegmentedSelectButtonsHeader from '~/UI/SegmentedSelect/ButtonsHeader/SegmentedSelectButtonsHeader';
import largeSegmentedSelectStyles from '~/UI/SegmentedSelect/large.less';
import SegmentedSelect, { SelectedOption } from '~/UI/SegmentedSelect/SegmentedSelect';

import styles from './styles.less';

interface Props {
	canCreateGroup: boolean;
	productsCount: number;
	isSaving: boolean;
	onSave?(group: UserProductGroup): void;
}

interface State {
	selectedOption: SelectedOption | null;
	showCreateGroupModal: (typeof PRODUCT_LIST_TYPE | typeof GROUP_TYPE) | null;
	createGroupParent: UserProductGroup | null;
	nameFilter: string;
}

export default class AddForm extends Component<Props, State> {
	state: State = {
		selectedOption: null,
		showCreateGroupModal: null,
		createGroupParent: null,
		nameFilter: '',
	};

	selectElement: SegmentedSelect | null = null;

	renderGroupsHeader() {
		const { canCreateGroup } = this.props;

		return (
			<SegmentedSelectButtonsHeader styles={largeSegmentedSelectStyles}>
				<Button disabled={!canCreateGroup} styles={buttonPrimaryAltMiniStyles} onClick={this.handleCreateGroupClick}>
					Create Folder
				</Button>
				<Button
					disabled={!canCreateGroup}
					styles={buttonSecondaryMiniStyles}
					onClick={() => this.handleCreateListClick(null)}
				>
					Create List
				</Button>
			</SegmentedSelectButtonsHeader>
		);
	}

	handleCreateGroupClick = () => {
		if (this.selectElement) {
			this.selectElement.collapse();
		}
		this.setState({
			showCreateGroupModal: GROUP_TYPE,
			createGroupParent: null,
		});
	};

	handleCreateListClick = (parent: UserProductGroup | null) => {
		if (this.selectElement) {
			this.selectElement.collapse();
		}
		this.setState({
			showCreateGroupModal: PRODUCT_LIST_TYPE,
			createGroupParent: parent,
		});
	};

	renderGroupHeader() {
		const {
			state: { selectedOption },
			props: { canCreateGroup },
		} = this;

		return (
			<SegmentedSelectButtonsHeader styles={largeSegmentedSelectStyles}>
				<Button styles={linkMiniStyles} onClick={() => this.setState({ selectedOption: null })}>
					BACK
				</Button>
				<Button
					disabled={!canCreateGroup}
					styles={buttonSecondaryMiniStyles}
					onClick={() => this.handleCreateListClick(selectedOption?.parentValue || null)}
				>
					Create List
				</Button>
			</SegmentedSelectButtonsHeader>
		);
	}

	handleNewGroupSave = (group: UserProductGroup) => {
		this.setState((prevState) => {
			const selectedOption = {
				value: group,
				parentValue: group.isGroup() ? group : prevState.createGroupParent,
				prompt: group.isGroup() ? null : group.name,
			};

			return {
				selectedOption,
				showCreateGroupModal: null,
				createGroupParent: null,
			};
		});

		if (group.isProductList()) {
			this.props.onSave?.(group);
		}
	};

	render() {
		const {
			props: { productsCount, isSaving, onSave },
			state: { selectedOption, showCreateGroupModal, createGroupParent, nameFilter },
		} = this;

		return (
			<Fragment>
				<ProductGroupSelect
					disabled={isSaving}
					groupHeader={this.renderGroupHeader()}
					groupsHeader={this.renderGroupsHeader()}
					nameFilter={nameFilter}
					selectedOption={selectedOption}
					selectRef={(ref) => (this.selectElement = ref)}
					onFilter={(nameFilter) => this.setState({ nameFilter, selectedOption: null })}
					onSelect={(selectedOption) => this.setState({ selectedOption })}
				/>

				<span className={styles.saveButtonContainer}>
					<Button
						dataTestId='add-form-save-button'
						disabled={selectedOption === null || selectedOption.value.isGroup() || isSaving}
						styles={buttonPrimarySmallStyles}
						onClick={() => onSave?.(selectedOption?.value)}
					>
						Save
					</Button>
				</span>

				<span className={styles.activityIndicatorContainer}>
					<SaveActivityIndicator status={isSaving ? Statuses.saving : null} />
				</span>

				{showCreateGroupModal !== null ? (
					<ProductGroupCreateModal
						groupType={showCreateGroupModal}
						parentId={createGroupParent !== null ? createGroupParent.id : null}
						saveButtonLabel={
							showCreateGroupModal === PRODUCT_LIST_TYPE
								? productsCount === 1
									? 'Add Product'
									: 'Add Products'
								: 'Save'
						}
						onClose={() => this.setState({ showCreateGroupModal: null })}
						onSave={this.handleNewGroupSave}
					/>
				) : null}
			</Fragment>
		);
	}
}
